import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Timer } from '../components/Timer';
import { Input, Select } from '../components/Input';
import { TodaySummary } from '../components/Reports';
import { Session } from '../types';
import { apiFetch } from '../services/api';
import type { ApiError } from '../services/api';

export const LandingPage: React.FC = () => {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const contactSectionRef = useRef<HTMLDivElement | null>(null);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
  const isTurnstileEnabled = Boolean(turnstileSiteKey);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTopic, setContactTopic] = useState('General Feedback');
  const [contactMessage, setContactMessage] = useState('');
  const [demoSessions] = useState<Session[]>([
    { id: '1', dateKey: '2023-10-01', startTime: 0, endTime: 0, durationMinutes: 20, productive: true, pauseCount: 0, totalPausedSeconds: 0 },
    { id: '2', dateKey: '2023-10-01', startTime: 0, endTime: 0, durationMinutes: 20, productive: false, pauseCount: 1, totalPausedSeconds: 45 },
    { id: '3', dateKey: '2023-10-01', startTime: 0, endTime: 0, durationMinutes: 20, productive: true, pauseCount: 0, totalPausedSeconds: 0 },
  ]);

  const loadTurnstileScript = () => {
    if (!isTurnstileEnabled || turnstileReady) {
      return;
    }
    const existing = document.querySelector('script[data-turnstile="true"]');
    if (existing) {
      setTurnstileReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = 'true';
    script.onload = () => setTurnstileReady(true);
    document.head.appendChild(script);
  };

  useEffect(() => {
    if (!isTurnstileEnabled) {
      return;
    }
    const target = contactSectionRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      loadTurnstileScript();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadTurnstileScript();
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [isTurnstileEnabled, turnstileReady]);

  useEffect(() => {
    if (!isTurnstileEnabled || !turnstileReady) {
      return;
    }

    let widgetId: string | undefined;
    let intervalId: number | undefined;

    const renderWidget = () => {
      if (!turnstileRef.current || !window.turnstile?.render) {
        return;
      }

      widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        theme: 'auto',
        callback: (token: string) => {
          setTurnstileToken(token);
          setFormError(null);
        },
        'expired-callback': () => {
          setTurnstileToken(null);
        },
        'error-callback': () => {
          setTurnstileToken(null);
          setFormError('Turnstile verification failed. Please try again.');
        },
      });
    };

    if (window.turnstile?.render) {
      renderWidget();
    } else {
      intervalId = window.setInterval(() => {
        if (window.turnstile?.render) {
          window.clearInterval(intervalId);
          renderWidget();
        }
      }, 300);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (widgetId && window.turnstile?.remove) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [isTurnstileEnabled, turnstileReady, turnstileSiteKey]);

  const handleContactSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(false);

    if (isTurnstileEnabled && !turnstileToken) {
      setFormError('Please complete the Turnstile check.');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiFetch('/contact', {
        method: 'POST',
        body: JSON.stringify({
          name: contactName,
          email: contactEmail,
          topic: contactTopic,
          message: contactMessage,
          turnstileToken,
        }),
      });
      setFormSuccess(true);
      setContactName('');
      setContactEmail('');
      setContactTopic('General Feedback');
      setContactMessage('');
      window.turnstile?.reset();
      setTurnstileToken(null);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.code === 'TURNSTILE_FAILED') {
        window.turnstile?.reset();
        setTurnstileToken(null);
      }
      setFormError(apiError.message || 'Unable to submit right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-12 space-y-24">
      {/* Hero */}
      <section className="pt-12 sm:pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h1 className="text-4xl sm:text-6xl font-extrabold text-text tracking-tight leading-tight">
              Track your focus <br/>
              <span className="text-primary">in minutes.</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted max-w-lg">
              Built for personal use only, the 20 Minutes Rule is a personal focus timer and Pomodoro alternative for focused
              people who want to timebox work with clarity. It asks one question every interval: "Did you work productively?"
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/signup">
                <Button size="lg">Start Focusing</Button>
              </Link>
            </div>
            <p className="text-xs sm:text-sm text-no max-w-lg">
              <span className="font-semibold uppercase tracking-wide">Disclaimer</span>
              <br />
              This application is designed for personal self reflection only. It may not be used to track, monitor, or
              evaluate other individuals. Misuse of the service may lead to account termination and further action where
              legally required.
            </p>
          </div>
          
          <div className="relative">
             <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-600 rounded-2xl blur opacity-20"></div>
             <div className="relative bg-surface rounded-2xl p-1 shadow-xl">
                <div className="bg-background rounded-xl p-4 sm:p-6" id="demo">
                    <div className="mb-4 flex items-center justify-between">
                         <span className="text-xs font-bold text-muted uppercase tracking-wider">Live Demo</span>
                         <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">Try me</span>
                    </div>
                    <Timer initialMinutes={20} size="compact" />
                    <div className="mt-8 pt-8 border-t border-border">
                        <p className="text-sm font-semibold mb-2 text-text">Today's Summary</p>
                        <TodaySummary sessions={demoSessions} />
                    </div>
                 </div>
             </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-surface py-24 border-y border-border">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-text mb-4">Simple, honest tracking.</h2>
                <p className="text-muted text-lg max-w-2xl mx-auto">
                  Short, time boxed sessions have a long history in productivity methods. Timeboxing sets a fixed window
                  for a task, and approaches like the Pomodoro Technique popularized 25 minute focus sprints. The 20
                  Minutes Rule is a 20 minute focus method that keeps the spirit but trims the interval to lower the
                  barrier to start, then uses a quick productivity check in to keep the habit honest.
                </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {[
                    { title: "1. Set & Start", desc: "Choose your focus interval (usually 20 mins) and start the timer. Work on one task only." },
                    { title: "2. Check In", desc: "When the alarm rings, answer one question: Was I productive? Yes or No." },
                    { title: "3. Review", desc: "See your productivity trends over time. Identify your best hours and improve." }
                ].map((step, i) => (
                    <div key={i} className="relative p-6 rounded-2xl bg-background border border-border hover:border-primary/50 transition-colors">
                        <div className="absolute -top-4 left-6 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                            {i + 1}
                        </div>
                        <h3 className="mt-4 text-xl font-bold text-text mb-2">{step.title}</h3>
                        <p className="text-muted leading-relaxed">{step.desc}</p>
                    </div>
                ))}
            </div>
         </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text mb-4">FAQ</h2>
          <p className="text-muted text-lg">Quick answers to common questions.</p>
        </div>
        <div className="space-y-3">
          {[
            {
              question: "What is the 20 Minutes Rule?",
              answer: "It is a simple focus loop. Work for 20 minutes, then answer one question about your focus.",
            },
            {
              question: "Is it completely free?",
              answer: "Yes, the core experience is free to use.",
            },
            {
              question: "Can I change the interval length?",
              answer: "Yes. You can set any interval between 5 and 120 minutes in the app.",
            },
            {
              question: "Do I need to allow sounds or notifications?",
              answer: "Yes. Allow sounds or notifications so you do not miss the alarm when an interval ends.",
            },
            {
              question: "How are my reports calculated?",
              answer: "Each completed interval logs a yes or no. Daily, weekly, monthly, and yearly summaries are based on those logs.",
            },
            {
              question: "Can I delete my data?",
              answer: "Yes. You can remove your report data at any time from the app.",
            },
          ].map((item) => (
            <details key={item.question} className="group rounded-xl border border-border bg-surface px-6 py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between text-base font-semibold text-text">
                {item.question}
                <span className="text-muted transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-muted leading-relaxed">{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section
        id="contact"
        ref={contactSectionRef}
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8"
        onFocusCapture={loadTurnstileScript}
        onMouseEnter={loadTurnstileScript}
      >
          <div className="bg-surface rounded-2xl shadow-sm border border-border p-8 sm:p-12">
            <div className="text-center mb-10">
                <h2 className="text-2xl font-bold text-text">Get in touch</h2>
                <p className="text-muted">Have feedback or found a bug? Let us know.</p>
            </div>
            <form className="space-y-6" onSubmit={handleContactSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Input
                      placeholder="Your Name"
                      required
                      value={contactName}
                      onChange={(event) => setContactName(event.target.value)}
                    />
                    <Input
                      type="email"
                      placeholder="Your Email"
                      required
                      value={contactEmail}
                      onChange={(event) => setContactEmail(event.target.value)}
                    />
                </div>
                <Select label="Topic" value={contactTopic} onChange={(event) => setContactTopic(event.target.value)}>
                    <option>General Feedback</option>
                    <option>Bug Report</option>
                    <option>Feature Request</option>
                    <option>Report Misuse</option>
                </Select>
                <textarea 
                    className="w-full px-3 py-2 bg-background border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-32" 
                    placeholder="Your message..."
                    required
                    value={contactMessage}
                    onChange={(event) => setContactMessage(event.target.value)}
                ></textarea>
                <div className="space-y-3">
                  {isTurnstileEnabled ? (
                    <div className="flex justify-center">
                      <div ref={turnstileRef} />
                    </div>
                  ) : (
                    <p className="text-xs text-muted">Turnstile is not configured in this environment.</p>
                  )}
                  {formError && <p className="text-xs text-no">{formError}</p>}
                  {formSuccess && <p className="text-xs text-yes">Thanks, your message is on the way.</p>}
                </div>
                <Button type="submit" size="lg" fullWidth disabled={isSubmitting || (isTurnstileEnabled && !turnstileToken)}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
            </form>
          </div>
      </section>
    </div>
  );
};
